import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  MessageSquare, Plus, RefreshCw, XCircle, Sparkles, Bug,
  Lock, User, Clock, CheckCircle2, AlertCircle,
} from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveEvents } from "@/lib/useLiveEvents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TicketAttachments from "./TicketAttachments";

/** Elegant status labels — the raw enum stays crew-friendly on the backend
 *  and the operator-facing wording is defined here. */
export const STATUS_META = {
  open:             { label: "Received",          tone: "bg-primary/15 text-primary border-primary/30" },
  under_review:     { label: "Under Consideration", tone: "bg-accent/15 text-accent border-accent/30" },
  in_development:   { label: "Under Development", tone: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30" },
  awaiting_payment: { label: "Payment Pending",   tone: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30" },
  shipped:          { label: "Delivered",         tone: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-500/40" },
  rejected:         { label: "Declined",          tone: "bg-destructive/15 text-destructive border-destructive/30" },
  cancelled:        { label: "Cancelled",         tone: "bg-muted text-muted-foreground border-border" },
  duplicate:        { label: "Merged (duplicate)", tone: "bg-muted text-muted-foreground border-border" },
};

const KIND_META = {
  feature_request: { label: "Upgrade / feature request", icon: Sparkles },
  bug_report:      { label: "Technical issue",           icon: Bug },
};

export function StatusBadge({ status, testId }) {
  const meta = STATUS_META[status] || STATUS_META.open;
  return (
    <Badge className={`text-[10px] border ${meta.tone} rounded-full`} data-testid={testId}>
      {meta.label}
    </Badge>
  );
}

/** Formats "3h ago" / "just now" without any date-fns dep. */
export function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const s = Math.max(1, Math.round((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function SupportPanel() {
  const { admin } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ kind: "feature_request", subject: "", body: "", attachments: [] });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [replySaving, setReplySaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/support/tickets/mine");
      setTickets(data || []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not load tickets");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Real-time updates — any support.* WS event from the server triggers a
  // silent reload so the crew's list stays fresh without a page refresh.
  // We also live-refresh the currently-open detail modal.
  useLiveEvents(useCallback(async (ev) => {
    if (!ev || !ev.type || !ev.type.startsWith("support.")) return;
    if (selected && (ev.ticket_id === selected.id || ev.ticket?.id === selected.id)) {
      try {
        const { data } = await api.get(`/support/tickets/${selected.id}`);
        setSelected(data);
      } catch (_) { /* silent */ }
    }
    // Refresh list in the background — most events touch the requester's rows.
    if (ev.requester_email === admin?.email || ev.ticket?.created_by_email === admin?.email) {
      try {
        const { data } = await api.get("/support/tickets/mine");
        setTickets(data || []);
      } catch (_) { /* silent */ }
    }
  }, [selected, admin?.email]));

  const submit = async () => {
    if (!form.subject.trim() || !form.body.trim()) {
      toast.error("Subject and description are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/support/tickets", form);
      toast.success("Ticket submitted");
      setOpenNew(false);
      setForm({ kind: "feature_request", subject: "", body: "", attachments: [] });
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not submit");
    } finally { setSaving(false); }
  };

  const openDetail = async (t) => {
    setSelected(t);
    setReplyText("");
    setReplyAttachments([]);
    try {
      // Refresh detail on open so comments are current.
      const { data } = await api.get(`/support/tickets/${t.id}`);
      setSelected(data);
    } catch (_) { /* ignore — the list snapshot is still shown */ }
  };

  const cancelTicket = async (t) => {
    if (!window.confirm(`Cancel "${t.subject}"? You can always open a new ticket later.`)) return;
    try {
      await api.post(`/support/tickets/${t.id}/cancel`);
      toast.success("Ticket cancelled");
      setSelected(null); load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not cancel");
    }
  };

  const reply = async () => {
    const body = replyText.trim();
    if ((!body && replyAttachments.length === 0) || !selected) return;
    setReplySaving(true);
    try {
      const { data } = await api.post(`/support/tickets/${selected.id}/comments`, {
        body: body || "(screenshot attached)",
        attachments: replyAttachments,
      });
      setSelected(data);
      setReplyText("");
      setReplyAttachments([]);
      load();
      toast.success("Comment added");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not add comment");
    } finally { setReplySaving(false); }
  };

  const grouped = useMemo(() => {
    const openLike = new Set(["open", "under_review", "in_development", "awaiting_payment"]);
    return {
      active: tickets.filter((t) => openLike.has(t.status)),
      closed: tickets.filter((t) => !openLike.has(t.status)),
    };
  }, [tickets]);

  return (
    <div data-testid="support-panel">
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-3xl">Support</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Ask for an upgrade or flag a technical issue. Signed in as <span className="text-foreground">{admin?.email}</span>{" "}
            · <span className="overline text-primary/50 text-[10px]">{admin?.role}</span>.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={load} data-testid="support-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button onClick={() => setOpenNew(true)} className="rounded-full bg-primary hover:bg-primary/90" data-testid="support-new-btn">
            <Plus className="h-4 w-4 mr-2" /> New request
          </Button>
        </div>
      </div>

      {loading && tickets.length === 0 ? (
        <div className="text-sm text-muted-foreground">Loading tickets…</div>
      ) : tickets.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="support-empty">
          <MessageSquare className="h-6 w-6 mx-auto mb-3 opacity-60" />
          <p className="text-sm">No tickets yet — click <span className="text-primary font-medium">New request</span> to raise one.</p>
        </div>
      ) : (
        <div className="space-y-10">
          <TicketList title="Active" items={grouped.active} onOpen={openDetail} />
          {grouped.closed.length > 0 && <TicketList title="Closed" items={grouped.closed} onOpen={openDetail} muted />}
        </div>
      )}

      {/* New ticket dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="max-w-lg" data-testid="support-new-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Raise a support ticket</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="overline text-primary/60">Type</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger className="mt-2" data-testid="support-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="feature_request">Upgrade / feature request</SelectItem>
                  <SelectItem value="bug_report">Technical issue</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline text-primary/60">Subject</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="One-line summary"
                maxLength={200}
                className="mt-2"
                data-testid="support-subject"
              />
            </div>
            <div>
              <Label className="overline text-primary/60">Description</Label>
              <Textarea
                rows={6}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="What did you expect? What's happening instead? Any screenshots or examples help."
                maxLength={5000}
                className="mt-2"
                data-testid="support-body"
              />
            </div>
            <div>
              <Label className="overline text-primary/60">Screenshots <span className="normal-case tracking-normal text-muted-foreground text-[10px]">(optional · up to 8)</span></Label>
              <div className="mt-2">
                <TicketAttachments
                  paths={form.attachments}
                  onChange={(next) => setForm({ ...form, attachments: next })}
                  testId="support-new-attachments"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpenNew(false)}>Cancel</Button>
              <Button onClick={submit} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="support-submit">
                {saving ? "Submitting…" : "Submit ticket"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ticket detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="support-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl flex items-start gap-3 flex-wrap">
                  <span className="min-w-0">{selected.subject}</span>
                  <StatusBadge status={selected.status} testId="support-detail-status" />
                </DialogTitle>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-3 mt-1">
                  <span className="font-mono">{selected.short_id}</span>
                  <span>{KIND_META[selected.kind]?.label}</span>
                  <span>Opened {relTime(selected.created_at)}</span>
                </div>
              </DialogHeader>

              <div className="space-y-6">
                <div className="border border-border p-4 bg-muted/30">
                  <div className="text-xs text-muted-foreground mb-1">Your original message</div>
                  <div className="text-sm whitespace-pre-wrap">{selected.body}</div>
                  {(selected.attachments || []).length > 0 && (
                    <div className="mt-3">
                      <TicketAttachments paths={selected.attachments} readOnly testId="support-orig-attachments" />
                    </div>
                  )}
                </div>

                <div>
                  <div className="overline text-primary/60 mb-3">Conversation</div>
                  {(selected.comments || []).length === 0 ? (
                    <div className="text-xs text-muted-foreground italic">No replies yet. The developer will respond as soon as it's picked up.</div>
                  ) : (
                    <ul className="space-y-3" data-testid="support-comments">
                      {(selected.comments || []).map((c) => {
                        const fromDev = c.author_role === "developer";
                        return (
                          <li
                            key={c.id}
                            className={`border p-3 ${fromDev ? "border-accent/40 bg-accent/5" : "border-border bg-card"}`}
                            data-testid={`support-comment-${c.id}`}
                          >
                            <div className="flex items-center gap-2 mb-1 text-[11px] text-muted-foreground">
                              <User className="h-3 w-3" />
                              <span className={fromDev ? "text-accent font-medium" : ""}>
                                {fromDev ? "Developer" : c.author_email}
                              </span>
                              <span>·</span>
                              <Clock className="h-3 w-3" />
                              <span>{relTime(c.created_at)}</span>
                              {c.private && (
                                <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 ml-2">
                                  <Lock className="h-3 w-3" /> private note
                                </span>
                              )}
                            </div>
                            <div className="text-sm whitespace-pre-wrap">{c.body}</div>
                            {(c.attachments || []).length > 0 && (
                              <div className="mt-2">
                                <TicketAttachments paths={c.attachments} readOnly testId={`support-comment-attachments-${c.id}`} />
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                {selected.status !== "cancelled" && (
                  <div className="border-t border-border pt-4">
                    <Label className="overline text-primary/60">Add a comment</Label>
                    <Textarea
                      rows={3}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Add more detail, or attach a screenshot below…"
                      className="mt-2"
                      data-testid="support-reply-body"
                    />
                    <div className="mt-2">
                      <TicketAttachments
                        paths={replyAttachments}
                        onChange={setReplyAttachments}
                        testId="support-reply-attachments"
                      />
                    </div>
                    <div className="flex justify-end mt-2">
                      <Button
                        onClick={reply}
                        disabled={replySaving || (!replyText.trim() && replyAttachments.length === 0)}
                        className="rounded-full bg-primary hover:bg-primary/90"
                        data-testid="support-reply-submit"
                      >
                        {replySaving ? "Sending…" : "Send comment"}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-border">
                  {selected.status !== "cancelled" && selected.status !== "shipped" && (
                    <Button
                      variant="outline"
                      onClick={() => cancelTicket(selected)}
                      className="rounded-full text-destructive border-destructive/40"
                      data-testid="support-cancel-btn"
                    >
                      <XCircle className="h-3.5 w-3.5 mr-2" /> Cancel ticket
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setSelected(null)} className="rounded-full">Close</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TicketList({ title, items, onOpen, muted }) {
  if (!items.length) return null;
  return (
    <div>
      <div className={`overline text-[10px] mb-3 ${muted ? "text-muted-foreground/60" : "text-primary/60"}`}>{title} · {items.length}</div>
      <ul className="space-y-2">
        {items.map((t) => {
          const Icon = KIND_META[t.kind]?.icon || MessageSquare;
          return (
            <li key={t.id}>
              <button
                onClick={() => onOpen(t)}
                className={`w-full text-left border border-border p-4 hover:border-primary transition-colors flex items-start gap-4 ${muted ? "opacity-70" : ""}`}
                data-testid={`support-ticket-${t.id}`}
              >
                <Icon className="h-4 w-4 mt-1 text-accent flex-shrink-0" strokeWidth={1.75} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">{t.subject}</span>
                    <StatusBadge status={t.status} />
                    {(t.comments || []).length > 0 && (
                      <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                        <MessageSquare className="h-3 w-3" /> {(t.comments || []).length}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                    <span className="font-mono">{t.short_id}</span>
                    <span>{KIND_META[t.kind]?.label}</span>
                    <span>Updated {relTime(t.updated_at)}</span>
                  </div>
                </div>
                {t.status === "shipped" ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                ) : t.status === "rejected" ? (
                  <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
